import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const COOKIE_CONSENT_STORAGE_KEY = 'cookieConsent.v1';
export const COOKIE_CONSENT_EVENT = 'cookie-consent-updated';

const CONSENT_VERSION = 1;
const isBrowser = typeof window !== 'undefined';

export const getCookieConsent = () => {
  if (!isBrowser) return null;

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (error) {
    console.error('Failed to parse cookie consent:', error);
    return null;
  }
};

export const hasAnalyticsConsent = () => {
  const consent = getCookieConsent();
  return Boolean(consent?.preferences?.analytics);
};

const saveCookieConsent = (preferences) => {
  if (!isBrowser) return;

  const payload = {
    version: CONSENT_VERSION,
    preferences: {
      necessary: true,
      analytics: Boolean(preferences.analytics),
    },
    updatedAt: new Date().toISOString(),
  };

  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: payload }));
};

function CookieConsent() {
  const { i18n } = useTranslation();
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isPreferenceModalOpen, setIsPreferenceModalOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  const copy = useMemo(() => {
    const isEnglish = i18n.language?.startsWith('en');
    return isEnglish
      ? {
          title: 'We use cookies',
          description:
            'We use necessary cookies to keep this website secure and functional. Analytics cookies are optional and help us improve the service.',
          acceptAll: 'Accept all',
          rejectOptional: 'Reject optional',
          customize: 'Customize',
          cancel: 'Cancel',
          settingsTitle: 'Cookie settings',
          necessary: 'Necessary cookies (always active)',
          analytics: 'Analytics cookies',
          save: 'Save preferences',
          manage: 'Cookie settings',
        }
      : {
          title: '我們使用 Cookie',
          description:
            '本站使用必要 Cookie 來維持安全與基本功能。分析 Cookie 為選用，用於改善服務體驗。',
          acceptAll: '全部接受',
          rejectOptional: '拒絕選用',
          customize: '自訂設定',
          cancel: '取消',
          settingsTitle: 'Cookie 設定',
          necessary: '必要 Cookie（永遠啟用）',
          analytics: '分析 Cookie',
          save: '儲存設定',
          manage: 'Cookie 設定',
        };
  }, [i18n.language]);

  useEffect(() => {
    const existingConsent = getCookieConsent();
    if (!existingConsent) {
      setIsBannerVisible(true);
      return;
    }

    setAnalyticsEnabled(Boolean(existingConsent.preferences.analytics));
  }, []);

  const applyConsent = (analytics) => {
    setAnalyticsEnabled(analytics);
    saveCookieConsent({ analytics });
    setIsBannerVisible(false);
    setIsPreferenceModalOpen(false);
  };

  return (
    <>
      {isBannerVisible && (
        <div className="fixed inset-x-0 bottom-0 z-[1200] border-t border-gray-200 bg-white/95 p-4 shadow-2xl backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/95">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <p className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">{copy.title}</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{copy.description}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => applyConsent(false)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {copy.rejectOptional}
              </button>
              <button
                type="button"
                onClick={() => setIsPreferenceModalOpen(true)}
                className="rounded-md border border-indigo-300 px-3 py-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-50 dark:border-indigo-500 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
              >
                {copy.customize}
              </button>
              <button
                type="button"
                onClick={() => applyConsent(true)}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-500"
              >
                {copy.acceptAll}
              </button>
            </div>
          </div>
        </div>
      )}

      {isPreferenceModalOpen && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl dark:bg-gray-900">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">{copy.settingsTitle}</h3>

            <div className="space-y-3">
              <div className="flex items-start justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{copy.necessary}</p>
                </div>
                <input type="checkbox" checked readOnly className="mt-1 h-4 w-4 cursor-not-allowed" />
              </div>

              <label className="flex items-start justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">{copy.analytics}</p>
                <input
                  type="checkbox"
                  checked={analyticsEnabled}
                  onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                  className="mt-1 h-4 w-4"
                />
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsPreferenceModalOpen(false)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {copy.cancel}
              </button>
              <button
                type="button"
                onClick={() => applyConsent(analyticsEnabled)}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-500"
              >
                {copy.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isBannerVisible && (
        <button
          type="button"
          onClick={() => setIsPreferenceModalOpen(true)}
          className="fixed bottom-4 left-4 z-[1100] rounded-full bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-lg transition hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600"
        >
          {copy.manage}
        </button>
      )}
    </>
  );
}

export default CookieConsent;
